import {
  FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Eye,
  EyeOff,
  LockKeyhole,
  LogOut,
  ShieldCheck,
} from 'lucide-react';

import { useDispatch } from 'react-redux';

import type { BackofficeAuthUser } from '../store/slices/authSlice';

import { logout } from '../store/slices/authSlice';

import { apiClient } from '../utils/api';

import {
  readCurrentShopIdFromToken,
} from '../utils/currentShop';

import {
  useCurrentShop,
} from '../contexts/CurrentShopContext';

const IDLE_TIMEOUT_MS =
  1 * 60 * 1000;

const ACTIVITY_EVENTS: Array<
  keyof WindowEventMap
> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
];

const STORAGE_KEY =
  'screen-saver-lock-state';

const STAFF_CODE_PATTERN =
  /^[A-Z0-9]{4}$/;

/*
 * =========================================================
 * ACTEUR AYANT DÉVERROUILLÉ
 * =========================================================
 *
 * id :
 *   identifiant technique de l'acteur.
 *
 * practitionerId :
 *   véritable ID dans la table practitioners.
 *
 * shopId :
 *   shop auquel l'acteur est rattaché.
 */
interface UnlockedActor {
  id: string;
  name: string;
  role: string;
  type: string;

  practitionerId?: string;
  employeeId?: string;
  shopId?: string;
}

interface ScreenSaverLockProps {
  user: BackofficeAuthUser | null;

  onUnlockedByChange?: (
    actor: UnlockedActor | null
  ) => void;
}

/*
 * =========================================================
 * LOCK STORAGE
 * =========================================================
 */

function readStoredLock(
  user: BackofficeAuthUser | null
) {
  if (!user) {
    return false;
  }

  try {
    const raw =
      localStorage.getItem(
        STORAGE_KEY
      );

    if (!raw) {
      return false;
    }

    const saved =
      JSON.parse(raw) as {
        userId?: string;
        shopId?: string | null;
        isLocked?: boolean;
      };

    const currentShopId =
      readCurrentShopIdFromToken();

    return (
      saved.userId === user.id &&
      saved.shopId ===
        currentShopId &&
      saved.isLocked === true
    );
  } catch {
    return false;
  }
}

/*
 * =========================================================
 * SESSION TYPE
 * =========================================================
 */

function isAdminSession(
  user: BackofficeAuthUser | null
) {
  return (
    user?.userType === 'ADMIN' ||
    !user?.userType
  );
}

/*
 * =========================================================
 * UNLOCK PAYLOAD
 * =========================================================
 */

function buildUnlockPayload(
  adminSession: boolean,
  value: string,
  shopId: string | null
) {
  const trimmed =
    value.trim();

  const payload: {
    shopId: string | null;
    password?: string;
    code?: string;
  } = {
    shopId,
  };

  /*
   * Une session admin accepte :
   *
   * - mot de passe
   * - ou code staff de 4 caractères
   */
  if (adminSession) {
    payload.password =
      trimmed;

    const codeCandidate =
      trimmed.toUpperCase();

    if (
      STAFF_CODE_PATTERN.test(
        codeCandidate
      )
    ) {
      payload.code =
        codeCandidate;
    }
  } else {
    /*
     * Practitioner / Employee :
     * uniquement code.
     */
    payload.code =
      trimmed.toUpperCase();
  }

  return payload;
}

/*
 * =========================================================
 * COMPONENT
 * =========================================================
 */

export function ScreenSaverLock({
  user,
  onUnlockedByChange,
}: ScreenSaverLockProps) {
  const dispatch =
    useDispatch();

  const {
    currentShop,
  } = useCurrentShop();

  const isEnabled =
    user?.permissions
      ?.screenSaverLock === true;

  const adminSession =
    isAdminSession(user);

  const displayName =
    user?.name ||
    user?.email ||
    'Utilisateur';

  const shopName =
    currentShop?.name ||
    'Shop courant';

  const timeoutRef =
    useRef<number | null>(
      null
    );

  const inputRef =
    useRef<HTMLInputElement>(
      null
    );

  const [
    isLocked,
    setIsLocked,
  ] = useState<boolean>(
    () =>
      readStoredLock(
        user
      )
  );

  const [
    unlockValue,
    setUnlockValue,
  ] = useState('');

  const [
    showUnlockValue,
    setShowUnlockValue,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState(false);

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  /*
   * =========================================================
   * PERSIST LOCK STATE
   * =========================================================
   */

  useEffect(() => {
    if (
      !user ||
      !isEnabled
    ) {
      setIsLocked(false);

      localStorage.removeItem(
        STORAGE_KEY
      );

      return;
    }

    const currentShopId =
      readCurrentShopIdFromToken();

    if (isLocked) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          userId:
            user.id,

          shopId:
            currentShopId,

          isLocked:
            true,
        })
      );

      return;
    }

    localStorage.removeItem(
      STORAGE_KEY
    );
  }, [
    user,
    isEnabled,
    isLocked,
  ]);

  /*
   * =========================================================
   * INACTIVITY TIMER
   * =========================================================
   */

  useEffect(() => {
    if (
      !user ||
      !isEnabled
    ) {
      if (
        timeoutRef.current
      ) {
        window.clearTimeout(
          timeoutRef.current
        );
      }

      return;
    }

    const lockSession =
      () => {
        setIsLocked(
          true
        );

        setUnlockValue(
          ''
        );

        setShowUnlockValue(
          false
        );

        setError(
          false
        );
      };

    const resetTimer =
      () => {
        if (isLocked) {
          return;
        }

        if (
          timeoutRef.current
        ) {
          window.clearTimeout(
            timeoutRef.current
          );
        }

        timeoutRef.current =
          window.setTimeout(
            lockSession,
            IDLE_TIMEOUT_MS
          );
      };

    resetTimer();

    ACTIVITY_EVENTS.forEach(
      (
        eventName
      ) => {
        window.addEventListener(
          eventName,
          resetTimer,
          {
            passive: true,
          }
        );
      }
    );

    return () => {
      if (
        timeoutRef.current
      ) {
        window.clearTimeout(
          timeoutRef.current
        );
      }

      ACTIVITY_EVENTS.forEach(
        (
          eventName
        ) => {
          window.removeEventListener(
            eventName,
            resetTimer
          );
        }
      );
    };
  }, [
    user,
    isEnabled,
    isLocked,
  ]);

  /*
   * =========================================================
   * AUTO FOCUS
   * =========================================================
   */

  useEffect(() => {
    if (isLocked) {
      inputRef.current
        ?.focus();
    }
  }, [isLocked]);

  /*
   * Rien à afficher si :
   *
   * - pas verrouillé
   * - pas utilisateur
   * - fonctionnalité désactivée
   */
  if (
    !isLocked ||
    !user ||
    !isEnabled
  ) {
    return null;
  }

  /*
   * =========================================================
   * LOGOUT
   * =========================================================
   */

  const handleLogout =
    () => {
      /*
       * On enlève également
       * l'acteur effectif côté UI.
       */
      onUnlockedByChange?.(
        null
      );

      dispatch(
        logout()
      );

      setIsLocked(
        false
      );

      setUnlockValue(
        ''
      );

      setShowUnlockValue(
        false
      );

      setError(
        false
      );

      localStorage.removeItem(
        STORAGE_KEY
      );

      window.location.href =
        '/login';
    };

  /*
   * =========================================================
   * UNLOCK
   * =========================================================
   */

  const handleSubmit =
    async (
      event: FormEvent<HTMLFormElement>
    ) => {
      event.preventDefault();

      if (
        isSubmitting ||
        !user ||
        !isEnabled
      ) {
        return;
      }

      if (
        !unlockValue.trim()
      ) {
        setError(
          true
        );

        inputRef.current
          ?.focus();

        return;
      }

      setIsSubmitting(
        true
      );

      setError(
        false
      );

      try {
        const currentShopId =
          readCurrentShopIdFromToken();

        const response =
          await apiClient(
            '/api/admin/screen-saver/unlock',
            {
              method:
                'POST',

              body:
                JSON.stringify(
                  buildUnlockPayload(
                    adminSession,
                    unlockValue,
                    currentShopId
                  )
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => null
            );

        const isValid =
          response.ok &&
          data?.success === true &&
          data?.valid === true;

        if (!isValid) {
          throw new Error(
            'Invalid unlock'
          );
        }

        /*
         * =====================================================
         * ACTEUR EFFECTIF
         * =====================================================
         *
         * Très important :
         *
         * On conserve maintenant :
         *
         * - id
         * - name
         * - role
         * - type
         * - practitionerId
         * - shopId
         *
         * Sans practitionerId, TimeOffManagerPage
         * ne peut pas créer correctement
         * practitioner_time_off.
         */
        if (
          data?.unlockMethod ===
            'code' &&
          data?.unlockedBy
        ) {
          const actor: UnlockedActor =
            {
              id:
                String(
                  data.unlockedBy
                    .id ||
                    ''
                ),

              name:
                String(
                  data.unlockedBy
                    .name ||
                    ''
                ),

              role:
                String(
                  data.unlockedBy
                    .role ||
                    ''
                ),

              type:
                String(
                  data.unlockedBy
                    .type ||
                    ''
                ),

              practitionerId:
                data
                  .unlockedBy
                  .practitionerId
                  ? String(
                      data
                        .unlockedBy
                        .practitionerId
                    )
                  : undefined,

              employeeId:
                data
                  .unlockedBy
                  .employeeId
                  ? String(
                      data
                        .unlockedBy
                        .employeeId
                    )
                  : undefined,

              shopId:
                data
                  .unlockedBy
                  .shopId
                  ? String(
                      data
                        .unlockedBy
                        .shopId
                    )
                  : undefined,
            };

          /*
           * TEMPORAIRE :
           *
           * À garder le temps de vérifier
           * la chaîne complète.
           */
          console.log(
            'SCREEN SAVER UNLOCKED BY:',
            actor
          );

          onUnlockedByChange?.(
            actor
          );
        } else {
          /*
           * Si le propriétaire de la session
           * déverrouille avec son mot de passe,
           * on revient à l'utilisateur normal.
           */
          onUnlockedByChange?.(
            null
          );
        }

        /*
         * =====================================================
         * DÉVERROUILLAGE UI
         * =====================================================
         */

        setIsLocked(
          false
        );

        setUnlockValue(
          ''
        );

        setShowUnlockValue(
          false
        );

        setError(
          false
        );

        localStorage.removeItem(
          STORAGE_KEY
        );
      } catch (error) {
        console.error(
          'Screen saver unlock failed:',
          error
        );

        setError(
          true
        );

        setUnlockValue(
          ''
        );

        setShowUnlockValue(
          false
        );

        inputRef.current
          ?.focus();

        setIsLocked(
          true
        );

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            userId:
              user.id,

            shopId:
              readCurrentShopIdFromToken(),

            isLocked:
              true,
          })
        );
      } finally {
        setIsSubmitting(
          false
        );
      }
    };

  /*
   * =========================================================
   * INPUT MODE
   * =========================================================
   */

  const isCodeOnlyInput =
    !adminSession;

  const inputLooksLikeCode =
    STAFF_CODE_PATTERN.test(
      unlockValue
        .trim()
        .toUpperCase()
    );

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-950 text-white px-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.24),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(245,158,11,0.18),transparent_34%)]" />

      <div className="relative w-full max-w-sm rounded-lg border border-white/10 bg-white/10 p-6 shadow-2xl backdrop-blur-md">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white text-slate-950">
            <LockKeyhole className="h-5 w-5" />
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-300">
              Ecran verrouille
            </p>

            <h2 className="text-lg font-semibold">
              {displayName}
            </h2>

            <p className="text-xs text-slate-300">
              {shopName}
            </p>
          </div>
        </div>

        <form
          onSubmit={
            handleSubmit
          }
          className="space-y-4"
        >
          <label
            className="block text-sm font-medium text-slate-200"
            htmlFor="screen-saver-unlock"
          >
            {adminSession
              ? 'Mot de passe ou code equipe'
              : 'Code du compte'}
          </label>

          <div className="relative">
            <input
              ref={inputRef}
              id="screen-saver-unlock"
              type={
                showUnlockValue
                  ? 'text'
                  : 'password'
              }
              value={
                unlockValue
              }
              onChange={(
                event
              ) => {
                const next =
                  isCodeOnlyInput
                    ? event.target.value
                        .toUpperCase()
                        .slice(0, 4)
                    : event.target
                        .value;

                setUnlockValue(
                  next
                );

                setError(
                  false
                );
              }}
              className={`w-full rounded-lg border border-white/20 bg-white py-3 pl-4 pr-11 text-slate-950 outline-none ring-2 ring-transparent transition focus:ring-sky-300 ${
                isCodeOnlyInput ||
                inputLooksLikeCode
                  ? 'text-center text-2xl font-semibold uppercase tracking-[0.35em]'
                  : 'text-base'
              }`}
              autoComplete={
                adminSession
                  ? 'current-password'
                  : 'off'
              }
              inputMode={
                isCodeOnlyInput ||
                inputLooksLikeCode
                  ? 'text'
                  : undefined
              }
              maxLength={
                isCodeOnlyInput
                  ? 4
                  : undefined
              }
            />

            <button
              type="button"
              onClick={() =>
                setShowUnlockValue(
                  (
                    visible
                  ) =>
                    !visible
                )
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700"
              aria-label={
                showUnlockValue
                  ? 'Masquer la saisie'
                  : 'Afficher la saisie'
              }
            >
              {showUnlockValue ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-200">
              {adminSession
                ? 'Mot de passe ou code incorrect.'
                : 'Le code est incorrect.'}
            </p>
          )}

          <button
            type="submit"
            disabled={
              isSubmitting
            }
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <ShieldCheck className="h-4 w-4" />

            {isSubmitting
              ? 'Validation...'
              : 'Deverrouiller'}
          </button>

          <button
            type="button"
            onClick={
              handleLogout
            }
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <LogOut className="h-4 w-4" />

            Se déconnecter
          </button>
        </form>
      </div>
    </div>
  );
}